import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { twMerge } from 'tailwind-merge';

// Named rather than written inline, so the generic never closes with a double
// angle bracket that some parsers read as a right shift.
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Hover tooltips only make sense with a precise, hovering pointer. On a touch
// device a tap synthesises a mouseenter (which would open the tooltip) but no
// mouseleave until you tap elsewhere, so it would linger stale; skip showing it
// there entirely (the trigger's aria-label still conveys the meaning).
var hasHoverPointer = function hasHoverPointer() {
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
};

// Keyboard focus, as opposed to focus that a click or a tap left behind.
var isKeyboardFocus = function isKeyboardFocus(target) {
  if (!(target instanceof Element)) {
    return false;
  }
  try {
    return target.matches(':focus-visible');
  } catch (_unused) {
    // Safari learned :focus-visible in 15.4, and matches() throws on a
    // selector it cannot parse. Where the browser cannot answer, treat
    // the focus as a keyboard one rather than lose the tooltip: Safari
    // does not focus a button on tap, so the tap this test guards against
    // does not arise there.
    return true;
  }
};
export var Tooltip = function Tooltip(_ref) {
  var children = _ref.children,
    label = _ref.label,
    _ref$placement = _ref.placement,
    placement = _ref$placement === void 0 ? 'bottom' : _ref$placement,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$tooltipClassName = _ref.tooltipClassName,
    tooltipClassName = _ref$tooltipClassName === void 0 ? '' : _ref$tooltipClassName;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    referenceRef = _useState2[0],
    setReferenceElement = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    popperRef = _useState4[0],
    setPopperElement = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    displayPopper = _useState6[0],
    setDisplayPopper = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    displayTooltip = _useState8[0],
    setDisplayTooltip = _useState8[1];
  // Every instance used to hardcode id="tooltip", so the ids collided and
  // aria-describedby always resolved to whichever one came first in the DOM.
  var tooltipId = useId();
  // Set only for *keyboard* focus. A ref, not state: the handlers read it
  // live and it must not re-run the watcher effect.
  var keyboardFocused = useRef(false);
  // Is the pointer over the trigger right now? Read by the reveal timer when
  // it fires, so a pointer that wandered off in the meantime gets no tooltip.
  var pointerInside = useRef(false);
  // The reveal wait, and the grace period before the popper unmounts. Held in
  // refs so they can be cancelled. They used to be a debounce call built
  // inline on each use, which makes a throwaway debounced function every
  // time: nothing holds it, so nothing can cancel it, and the timer from the
  // previous interaction still runs.
  var revealTimer = useRef(undefined);
  var hideTimer = useRef(undefined);
  var clearTimers = function clearTimers() {
    clearTimeout(revealTimer.current);
    clearTimeout(hideTimer.current);
    revealTimer.current = undefined;
    hideTimer.current = undefined;
  };

  // A reveal already counting down keeps its original deadline, so a pointer
  // that wobbles out and back does not restart the wait. The timer decides
  // when it fires whether the pointer is still there.
  var scheduleReveal = function scheduleReveal() {
    if (revealTimer.current) {
      return;
    }
    revealTimer.current = setTimeout(function () {
      revealTimer.current = undefined;
      if (pointerInside.current) {
        setDisplayTooltip(true);
      }
    }, 100);
  };
  useEffect(function () {
    return clearTimers;
  }, []);
  var getPlacement = function getPlacement(p) {
    switch (p) {
      case 'start':
        return 'left';
      case 'end':
        return 'right';
      default:
        return p;
    }
  };
  var _usePopper = usePopper(referenceRef, popperRef, {
      placement: getPlacement(placement),
      modifiers: [{
        name: 'offset',
        options: {
          offset: [0, 4]
        }
      }, {
        name: 'preventOverflow',
        options: {
          padding: 8,
          rootBoundary: 'document'
        }
      }]
    }),
    styles = _usePopper.styles,
    attributes = _usePopper.attributes;

  // While open, close as soon as the pointer is no longer over the trigger.
  // onMouseLeave alone misses the case where the trigger moves out from under
  // a stationary cursor, such as a layout reflow (the tile relocating on
  // pin, resize, or participants joining or leaving), which would otherwise
  // strand the tooltip open. Gated on displayPopper, so a mounted but closed
  // tooltip adds nothing; only the one open tooltip carries a listener.
  useEffect(function () {
    if (!displayPopper || !referenceRef) {
      return undefined;
    }
    var closeIfPointerOutside = function closeIfPointerOutside(event) {
      // Checked here rather than gating the whole effect: clicking a
      // control focuses it, so gating on focus would kill this watcher
      // for every mouse interaction, including the click-driven
      // reflows (pin / unpin) it exists for.
      if (keyboardFocused.current) {
        return;
      }
      var rect = referenceRef.getBoundingClientRect();
      var isInside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!isInside) {
        pointerInside.current = false;
        clearTimeout(revealTimer.current);
        clearTimeout(hideTimer.current);
        revealTimer.current = undefined;
        hideTimer.current = undefined;
        setDisplayTooltip(false);
        setDisplayPopper(false);
      }
    };
    document.addEventListener('mousemove', closeIfPointerOutside);
    return function () {
      return document.removeEventListener('mousemove', closeIfPointerOutside);
    };
  }, [displayPopper, referenceRef]);

  // The description has to sit on the element that actually takes focus. The
  // wrapper is a roleless generic div, so a description on it is never
  // reached and never announced. Only while the tooltip is mounted, or the
  // reference dangles.
  //
  // Memoised, and above the early return so it stays an unconditional hook.
  // Cloning produces a new element, which costs the wrapped control a render:
  // without this, every tooltip render while open re-cloned and re-rendered
  // it: five times per hover rather than the two the attribute itself needs.
  var describedChildren = useMemo(function () {
    return displayPopper && /*#__PURE__*/React.isValidElement(children) ? /*#__PURE__*/React.cloneElement(children, {
      'aria-describedby': tooltipId
    }) : children;
  }, [children, displayPopper, tooltipId]);

  // Nothing to say, nothing to wrap. Placed after the hooks above so their
  // order never changes.
  if (!label) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  var handleMouseEnter = function handleMouseEnter() {
    if (!hasHoverPointer()) {
      return;
    }
    pointerInside.current = true;
    clearTimeout(hideTimer.current);
    hideTimer.current = undefined;
    setDisplayPopper(true);
    scheduleReveal();
  };

  // Focus outranks hover: moving the pointer across a control the keyboard is
  // already on must not dismiss its tooltip.
  var handleMouseLeave = function handleMouseLeave() {
    if (keyboardFocused.current) {
      return;
    }
    pointerInside.current = false;
    setDisplayTooltip(false);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(function () {
      return setDisplayPopper(false);
    }, 100);
  };

  // Focus parity with hover (WCAG 1.4.13). React's onFocus and onBlur bubble,
  // so these fire for the wrapped control, which is the thing that actually
  // takes focus. Only the keyboard path opens it: clicking or tapping also
  // focuses, and popping a tooltip open on every tap is what the pointer
  // guard avoids.
  var handleFocus = function handleFocus(event) {
    if (!isKeyboardFocus(event.target)) {
      return;
    }
    clearTimers();
    keyboardFocused.current = true;
    setDisplayPopper(true);
    setDisplayTooltip(true);
  };
  var handleBlur = function handleBlur() {
    if (!keyboardFocused.current) {
      return;
    }
    clearTimers();
    keyboardFocused.current = false;
    setDisplayTooltip(false);
    setDisplayPopper(false);
  };

  // The wrapper below is deliberately a roleless div with no tabIndex, even
  // though it carries mouse and focus handlers, so it is marked NOSONAR.
  // Static analysis reads it as a non-native interactive element and asks for
  // a role and a tab stop, but that is precisely the bug this component
  // removes: a focusable wrapper put an unlabelled, inert tab stop in front
  // of every tooltipped control. The handlers only observe hover and focus on
  // behalf of the control inside.
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      // NOSONAR
      ref: setReferenceElement,
      className: twMerge('flex', tooltipClassName),
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onFocus: handleFocus,
      onBlur: handleBlur,
      children: describedChildren
    }), displayPopper && /*#__PURE__*/createPortal(/*#__PURE__*/_jsx("div", _objectSpread(_objectSpread({
      id: tooltipId,
      ref: setPopperElement,
      style: styles.popper
    }, attributes.popper), {}, {
      className: twMerge('z-50 max-w-xs rounded-sm bg-gray-800 px-1.5 py-0.5 text-xs/5 font-normal text-white shadow-xs transition-opacity duration-300', displayTooltip ? 'opacity-100' : 'opacity-0', className),
      role: "tooltip",
      children: label
    })), document.getElementById('tooltipPortal'))]
  });
};
export default Tooltip;